import React, {
  createRef,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Case from 'case';

import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import getPageImages from '../Widgets/Images';
import Text from '../Widgets/Text';
import Components from './components';
import VerifyIdentityExplainer from '../Widgets/VerifyIdentityExplainer';
import Message from '../../components/UI/Message';
import Button from '../../components/UI/Button';
import layoutCSS from '../Layout/Layout.module.scss';
import { isPOBox } from '../../helpers';
import IdentityLoader from '../../components/UI/IdentityLoader/index';
import { AppType } from '../../store/store';
import Alert from '../../components/UI/Alert';
import { useApp } from '../../state/app';
import { useToggles } from '../../state/toggles';
import { useUser } from '../../state/user';
import { CreditApplicationStatus } from '../../services/credit';
import BannerMessageHorizontal from '../../components/UI/BannerMessageHorizontal';
import { useRouter } from "next/router";

interface Props {
  app: AppType;
  toggles: Record<string, any>;
  overriddenGroupId: string;
  resolvesErrorId: number;
  questions: string[];
  questionsOverride: boolean;
  questionsOverrideReason?: string;
  onSubmit: (questions: string[], answers: string[]) => void;
  onClick: (widget: string) => void;
  displayLegalAddress: (toggle: boolean) => void;
  onSetSsn: (ssn: string) => void;
  onSetConfirmIdAnswers: (confirmIdAnswers: string[]) => void;
}

type EnabledStatus = { [key: string]: boolean };

type Answers = { [key: string]: any };

export interface WidgetPageComponentProps extends Props {
  changeAnswerValue: (question: string, answer: any) => void;
  onClick: () => void;
  shouldEnable: (question: string, enabled: boolean) => void;
  index: number;
  widget: string;
  questionsAndAnswers: Answers;
}

const SSN_WIDGET = 'set_user_ssn_last4';
const PROVE_PREFILL_WIDGET = 'prove_prefill';

function WidgetWrapper(props: Props) {
  const {
    app: { flowId }
  } = props;
  const groupId = props.overriddenGroupId;
  const { isFirstScreenForReturningUser, shouldRedirectToLta, ltaRedirectParams  } = useApp();
  const { planFlow } = useToggles();
  const { registeredUser } = useUser();
  const router = useRouter();

  const [answers, setAnswers] = useState<Answers>();
  const [enabledStatus, setEnabledStatus] = useState<EnabledStatus>();
  const refs = useRef(props.questions.map(() => createRef()));
  const setPrefillAnswers = () => {
    const prefillAnswers = props.app.prefilledAnswers;
    const preregOnAddChild =
      props.questions.indexOf('add_child_info') !== -1 &&
      (get(props.app, 'preregisteredUser.children') ||
        get(props.app, 'registeredUser.user.children'));
    if (!isEmpty(prefillAnswers) && !preregOnAddChild) {
      let enabledStatusWithPrefill = enabledStatus;
      let answersWithPrefill = answers;
      // eslint-disable-next-line no-restricted-syntax
      for (const [question, answer] of Object.entries(prefillAnswers)) {
        if (props.questions.indexOf(Case.snake(question)) !== -1) {
          enabledStatusWithPrefill = {
            ...enabledStatusWithPrefill,
            [Case.snake(question)]: true
          };
          answersWithPrefill = {
            ...answersWithPrefill,
            [Case.snake(question)]: answer
          };
        }
      }
      setEnabledStatus(enabledStatusWithPrefill);
      setAnswers(answersWithPrefill);
    }
  };

  useEffect(() => {
    if (shouldRedirectToLta) {
      router.replace({
        pathname: '/lta-redirect',
        query: ltaRedirectParams
      });
    }
  }, [shouldRedirectToLta, ltaRedirectParams]);

  useEffect(() => {
    const createInitialStatus = props.questions.reduce(
      (acc: Answers, question: string) => {
        acc[question] = false;
        return acc;
      },
      {}
    );

    // Should alway be true
    createInitialStatus.pma_phone_number = true;

    // eslint-disable-next-line no-prototype-builtins
    if (createInitialStatus.hasOwnProperty('set_autofunding_preference')) {
      createInitialStatus.set_autofunding_preference = true;
    }
    // eslint-disable-next-line no-prototype-builtins
    if (createInitialStatus.hasOwnProperty('set_referral_code')) {
      createInitialStatus.set_referral_code = true;
    }
    setEnabledStatus(createInitialStatus);
  }, [props.questions]);

  useEffect(() => {
    const createInitialStatus = props.questions.reduce(
      (acc: Answers, question: string) => {
        acc[question] = false;
        return acc;
      },
      {}
    );

    // Should alway be true
    createInitialStatus.pma_phone_number = true;

    if (isEqual(enabledStatus, createInitialStatus) && enabledStatus) {
      setPrefillAnswers();
    }
  }, [enabledStatus]);

  const isNextButtonDisabled = useMemo(
    () =>
      enabledStatus ? !Object.values(enabledStatus).every(q => !!q) : false,
    [enabledStatus]
  );

  const checkErrors = async () => {
    try {
      await Promise.all(
        refs.current.map(ref => {
          if (ref.current) {
            const { errorChecking } = ref.current as any;
            if (typeof errorChecking === 'function') {
              return errorChecking();
            }
            return false;
          }
          return false;
        })
      );

      if (answers && answers.set_legal_address) {
        if (isPOBox(answers.set_legal_address.address)) {
          props.onSetConfirmIdAnswers(props.questions.map(q => answers[q]));
          props.displayLegalAddress(true);
        } else {
          const answersWithShippingAddress = answers
            ? props.questions.map(q => answers[q])
            : [];
          if (answers) {
            answersWithShippingAddress.push(answers.set_legal_address);
          }
          const questionsWithShippingAddress = Array.from(props.questions);
          questionsWithShippingAddress.push('set_shipping_address');
          props.onSubmit(
            questionsWithShippingAddress,
            answersWithShippingAddress
          );
        }
      } else {
        props.onSubmit(
          props.questions,
          answers ? props.questions.map(q => answers[q]) : []
        );
      }
    } catch (error) {
      window.scrollTo(0, 0);
    }
  };

  const onAnswerChanged = (question: string, answer: any) => {
    setAnswers({ ...answers, [question]: answer });
    if (question === SSN_WIDGET) props.onSetSsn(answer);
  };

  const onEnableChanged = (question: string, enabled: boolean) => {
    setEnabledStatus({ ...enabledStatus, [question]: !!enabled });
  };

  const questionsDoNotInclude = (question: string) =>
    props.questions.indexOf(question) === -1;

  const onKeyDown = (event: React.KeyboardEvent) => {
    // prove_review has it's own form that shouldn't be submitted from this
    if (event.key === 'Enter' && questionsDoNotInclude('prove_review')) {
      event.preventDefault();
      event.stopPropagation();
      if (!isNextButtonDisabled) checkErrors();
    }
  };

  const showNextButton =
    questionsDoNotInclude('complete') &&
    questionsDoNotInclude('set_funding_account') &&
    (questionsDoNotInclude('prove_prefill') || props.questions.length !== 1) &&
    questionsDoNotInclude('prove_review') &&
    questionsDoNotInclude('set_plan') &&
    questionsDoNotInclude('credit_application');

  const getImage = (imageType: string) => {
    const maxFlow =
      get(props, 'featureToggles.planFlow.enabled') &&
      get(props, 'featureToggles.planFlow.variables.planName') === 'max';

    const fccSecureImage =
      get(props, 'app.answers.creditApplication.creditAccepted') ||
      get(props, 'app.answers.creditApplication.creditDecision') ===
        CreditApplicationStatus.DELAYED;

    const images = getPageImages(
      props.toggles.smsNotificationsEnabled,
      maxFlow,
      fccSecureImage
    );
    return (images as any)[flowId!] && (images as any)[flowId!][groupId]
      ? (images as any)[flowId!][groupId][imageType]
      : (images as any).default_group[imageType];
  };

  const getComponentToRender = (question: string) => {
    return (Components as any)[Case.pascal(question)];
  };

  const fetchingProveData =
    props.app.loading &&
    props.questions.includes('prove_prefill') &&
    props.questions.length === 1 &&
    get(answers, 'prove_prefill.dob');

  const fetchingKycData =
    props.app.answerLoading && props.questions.includes('set_user_ssn_last4');

  if (fetchingProveData || fetchingKycData) {
    return <IdentityLoader />;
  }

  const showPlanFlowReturningUserMessage =
    isFirstScreenForReturningUser &&
    planFlow.enabled &&
    planFlow.variables?.planName === 'max' &&
    registeredUser?.user?.pricingPlan?.current?.planName === 'max';

  if (shouldRedirectToLta) {
    return null;
  }

  return (
    <div onKeyDown={onKeyDown}>
      <div className={layoutCSS.layoutWrapper}>
        {showPlanFlowReturningUserMessage && (
          <Alert
            heading="You've chosen Greenlight Max"
            message="Finish registering to start using investing and more."
            color="green"
            icon="greenCheck"
          />
        )}
        {questionsDoNotInclude('complete') && (!props.questionsOverride) && (
          <div style={{ textAlign: 'left' }}>
            <Text groupId={groupId} flowId={flowId!} {...props} />
            {props.questions.indexOf(SSN_WIDGET) !== -1 && (
              <VerifyIdentityExplainer explainerType="ssn" />
            )}
            {props.questions.indexOf(PROVE_PREFILL_WIDGET) !== -1 &&
              props.questions.length === 1 && (
                <VerifyIdentityExplainer explainerType="prove" />
              )}
          </div>
        )}
        {questionsDoNotInclude('complete') &&
          (props.app.error || props.app.message) && (
            <Message
              error={props.app.error}
              text={props.app.message || 'Please correct fields below'}
            />
          )}
        {getImage('topImage')}
        {props.questionsOverride && props.questionsOverrideReason === 'CORE_PRICE_CHANGE' && (
          <BannerMessageHorizontal
            header="Core plan price increase"
            description="Since your last plan selection, we have increased our pricing for Greenlight Core from $4.99 to $5.99 per month."
            icon={<img src="../../static/ic-dollar.svg" />}
          />
        )}
        <div>
          {props.questions.map((question, index) => {
            const ComponentToRender = getComponentToRender(question);
            return ComponentToRender ? (
              <Fragment key={index}>
                <Text
                  groupId={groupId}
                  flowId={flowId!}
                  {...props}
                  widgetIndex={index}
                />
                <ComponentToRender
                  ref={refs.current[index]}
                  {...props}
                  shouldEnable={onEnableChanged}
                  widget={question}
                  changeAnswerValue={onAnswerChanged}
                  onClick={() => props.onClick(question)}
                  index={index}
                  questionsAndAnswers={answers || {}}
                  onSetSsn={props.onSetSsn}
                  submitAnswers={checkErrors}
                />
              </Fragment>
            ) : (
              <div key={index} />
            );
          })}
        </div>
        {showNextButton && (
          <Button
            text="Next"
            loading={props.app.loading}
            disabled={isNextButtonDisabled}
            onClick={checkErrors}
          />
        )}
        {getImage('terms')}
      </div>
      {getImage('bottomImage')}
    </div>
  );
}

export default React.memo(WidgetWrapper);
